import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Call, Device as DeviceType } from '@twilio/voice-sdk';

type PageStatus = 'idle' | 'loading' | 'ready' | 'ringing' | 'connected' | 'completed' | 'error' | 'expired';

const RING_OSC = [440, 480];

function startRingtone(ctx: AudioContext, dest: AudioNode) {
  const osc1 = ctx.createOscillator();
  const osc2 = ctx.createOscillator();
  const gain = ctx.createGain();
  osc1.type = 'sine';
  osc2.type = 'sine';
  osc1.frequency.value = RING_OSC[0];
  osc2.frequency.value = RING_OSC[1];
  gain.gain.value = 0.15;
  osc1.connect(gain);
  osc2.connect(gain);
  gain.connect(dest);
  osc1.start();
  osc2.start();
  return { osc1, osc2, gain };
}

function stopRingtone(ring: ReturnType<typeof startRingtone>) {
  try { ring.osc1.stop(); } catch { /* ignore */ }
  try { ring.osc2.stop(); } catch { /* ignore */ }
  ring.osc1.disconnect();
  ring.osc2.disconnect();
  ring.gain.disconnect();
}

export function CallReceiverPage() {
  const { t } = useTranslation();
  const { sessionId } = useParams<{ sessionId: string }>();
  const [status, setStatus] = useState<PageStatus>('idle');
  const statusRef = useRef(status);
  useEffect(() => { statusRef.current = status; }, [status]);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [digits, setDigits] = useState('');
  const [msg, setMsg] = useState('');
  const deviceRef = useRef<DeviceType | null>(null);
  const connectionRef = useRef<Call | null>(null);
  const ringtoneRef = useRef<ReturnType<typeof startRingtone> | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);

  const setMessage = useCallback((m: string) => setMsg(m), []);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (['ready', 'ringing', 'connected'].includes(statusRef.current)) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  useEffect(() => {
    if (status === 'ringing' && !ringtoneRef.current) {
      const ctx = new AudioContext();
      audioCtxRef.current = ctx;
      ringtoneRef.current = startRingtone(ctx, ctx.destination);
    } else if (status !== 'ringing' && ringtoneRef.current) {
      stopRingtone(ringtoneRef.current);
      ringtoneRef.current = null;
      audioCtxRef.current?.close().catch(() => {});
      audioCtxRef.current = null;
    }
    return () => {
      if (ringtoneRef.current) {
        stopRingtone(ringtoneRef.current);
        ringtoneRef.current = null;
      }
      audioCtxRef.current?.close().catch(() => {});
      audioCtxRef.current = null;
    };
  }, [status]);

  const activate = useCallback(async () => {
    if (!sessionId) return;
    try {
      setStatus('loading');
      const res = await fetch(`/api/v1/voice/session/${sessionId}/token`);
      const data = await res.json();

      if (data.status !== 'success') {
        setStatus('expired');
        return;
      }

      setPhoneNumber(data.data.phoneNumber);
      const token = data.data.twilioToken;

      const { Device } = await import('@twilio/voice-sdk');
      const device: DeviceType = new Device(token, { logLevel: 'error' });
      deviceRef.current = device;

      device.on('registered', () => {
        setStatus('ready');
        setMessage(t('voice.ready'));
      });

      device.on('incoming', (call: Call) => {
        connectionRef.current = call;
        setStatus('ringing');
        setMessage(t('voice.incoming'));

        setTimeout(() => {
          if (statusRef.current === 'ringing') {
            call.reject();
            setStatus('completed');
            setMessage(t('voice.missed'));
          }
        }, 60000);
      });

      device.on('error', () => {
        setStatus('error');
        setMessage(t('voice.error'));
      });

      await device.register();
    } catch {
      setStatus('error');
      setMessage(t('voice.error'));
    }
  }, [sessionId, t]);

  const answer = useCallback(async () => {
    if (!connectionRef.current || !sessionId) return;
    connectionRef.current.accept();
    setStatus('connected');
    setMessage(t('voice.connected'));

    await fetch(`/api/v1/voice/session/${sessionId}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'connected' }),
    });

    connectionRef.current.on('disconnect', async () => {
      setStatus('completed');
      setMessage(t('voice.completed'));
      await fetch(`/api/v1/voice/session/${sessionId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'completed' }),
      });
    });
  }, [sessionId]);

  const pressDigit = (d: string) => setDigits((prev) => prev + d);
  const clearDigit = () => setDigits((prev) => prev.slice(0, -1));

  const sendDigits = useCallback(() => {
    if (connectionRef.current && digits) {
      connectionRef.current.sendDigits(digits);
      setMessage(`${t('voice.sent')}: ${digits}`);
      setDigits('');
    }
  }, [digits, t]);

  const hangup = useCallback(async () => {
    connectionRef.current?.disconnect();
    setStatus('completed');
    if (sessionId) {
      await fetch(`/api/v1/voice/session/${sessionId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'completed' }),
      });
    }
  }, [sessionId]);

  const statusConfig: Record<PageStatus, { icon: string; text: string; color: string }> = {
    idle: { icon: '📱', text: t('voice.statusIdle'), color: '#888' },
    loading: { icon: '⏳', text: t('voice.statusLoading'), color: '#A8E0E0' },
    ready: { icon: '✅', text: t('voice.statusReady'), color: '#00c853' },
    ringing: { icon: '📞', text: t('voice.statusRinging'), color: '#ff9100' },
    connected: { icon: '🔊', text: t('voice.statusConnected'), color: '#00c853' },
    completed: { icon: '✅', text: t('voice.statusCompleted'), color: '#00c853' },
    error: { icon: '❌', text: t('voice.statusError'), color: '#ff1744' },
    expired: { icon: '❌', text: t('voice.statusExpired'), color: '#ff1744' },
  };

  const cur = statusConfig[status];

  return (
    <div className="min-h-screen bg-brand-primary flex items-center justify-center p-4" dir="rtl">
      <div className="w-full max-w-sm">
        <div className="card-glass rounded-2xl p-6 text-center">
          <div className="text-5xl mb-4" style={{ animation: status === 'ringing' ? 'pulse-glow 0.8s ease-in-out infinite' : 'none' }}>
            {cur.icon}
          </div>
          <h1 className="text-lg font-heading font-bold text-brand-accent mb-1">
            {t('voice.receiverTitle')}
          </h1>
          <p className="text-xs text-neutral-medium font-body mb-6">
            {t('voice.receiverSubtitle')}
          </p>

          <div className="bg-neutral-dark/50 rounded-xl p-4 mb-4">
            <p className="text-sm font-bold" style={{ color: cur.color }}>{cur.text}</p>
            {msg && <p className="text-xs text-neutral-medium mt-1">{msg}</p>}
          </div>

          {phoneNumber && (
            <div className="text-lg font-mono font-bold text-brand-accent bg-neutral-dark/30 rounded-lg p-3 mb-4 select-all">
              {phoneNumber}
            </div>
          )}

          {status === 'idle' && (
            <button onClick={activate} className="w-full py-3 px-6 bg-brand-accent text-brand-primary font-heading font-bold rounded-xl hover:shadow-lg hover:shadow-brand-accent/50 transition-all duration-300">
              🔊 {t('voice.activate')}
            </button>
          )}

          {status === 'ringing' && (
            <button onClick={answer} className="w-full py-3 px-6 bg-green-600 text-white font-heading font-bold rounded-xl animate-pulse transition-all duration-300">
              ✅ {t('voice.answer')}
            </button>
          )}

          {status === 'connected' && (
            <div className="space-y-4">
              <div className="bg-neutral-dark/50 rounded-xl p-3">
                <p className="text-xl font-mono text-brand-accent tracking-widest min-h-[40px]">
                  {digits || t('voice.enterDigits')}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {['1','2','3','4','5','6','7','8','9'].map((d) => (
                  <button key={d} onClick={() => pressDigit(d)}
                    className="py-4 text-xl font-bold bg-neutral-dark text-neutral-light rounded-xl hover:bg-neutral-dark/80 active:scale-95 transition-all">
                    {d}
                  </button>
                ))}
                <button onClick={clearDigit}
                  className="py-4 text-xl bg-neutral-dark text-orange-400 rounded-xl hover:bg-neutral-dark/80 active:scale-95 transition-all">
                  ⌫
                </button>
                <button onClick={() => pressDigit('0')}
                  className="py-4 text-xl font-bold bg-neutral-dark text-neutral-light rounded-xl hover:bg-neutral-dark/80 active:scale-95 transition-all">
                  0
                </button>
                <button onClick={sendDigits}
                  className="py-4 text-xl bg-orange-500 text-white rounded-xl hover:bg-orange-600 active:scale-95 transition-all font-bold">
                  ✉️
                </button>
              </div>
              <button onClick={hangup} className="w-full py-3 bg-red-600 text-white font-heading font-bold rounded-xl hover:bg-red-700 transition-all duration-300">
                ❌ {t('voice.hangup')}
              </button>
            </div>
          )}

          {['completed', 'expired'].includes(status) && (
            <p className="text-xs text-neutral-medium font-body mt-4">
              {t('voice.closePage')}
            </p>
          )}
        </div>

        {['idle', 'ready', 'ringing', 'connected'].includes(status) && (
          <div className="card-glass rounded-xl p-4 mt-4 text-xs text-neutral-medium font-body space-y-1">
            <p className="text-amber-400 font-heading font-semibold mb-2">⚠️ {t('voice.warnings')}</p>
            <p>• {t('voice.warning1')}</p>
            <p>• {t('voice.warning2')}</p>
            <p>• {t('voice.warning3')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
